import { useEffect, useMemo, useRef } from 'react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  DEFAULT_CRM_CONFIG,
  normalizeCrmConfig,
  buildCrmEntityPrefixes,
} from './crmConfigDefaults';

let hydratePromise = null;

function hydrateCrmConfig(patchSettings, existingSettings) {
  if (!hydratePromise) {
    hydratePromise = localApiAdapter
      .get('/crm/config')
      .then((response) => {
        const raw = response?.data !== undefined ? response.data : response;
        const normalized = normalizeCrmConfig(raw || {});
        syncCrmConfigToSettings(normalized, patchSettings, existingSettings);
        return normalized;
      })
      .catch(() => DEFAULT_CRM_CONFIG)
      .finally(() => {
        hydratePromise = null;
      });
  }
  return hydratePromise;
}

/** Bridge CRM module config into SettingsContext for entity prefixes & live thresholds. */
export function syncCrmConfigToSettings(config, patchSettings, existingSettings) {
  const normalized = normalizeCrmConfig(config);
  patchSettings({
    crm: normalized,
    configuration: {
      entityPrefixes: {
        ...(existingSettings?.configuration?.entityPrefixes || {}),
        ...buildCrmEntityPrefixes(normalized),
      },
    },
  }, { persist: false });
  return normalized;
}

/** Live CRM module config — synced from API once, then from SettingsContext. */
export function useCrmConfig() {
  const { settings, patchSettings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(() => {
    const crm = settings?.crm;
    if (crm && Object.keys(crm).length > 0) return;
    void hydrateCrmConfig(patchSettings, settingsRef.current);
  }, [settings?.crm, patchSettings]);

  return useMemo(
    () => normalizeCrmConfig(settings?.crm || {}),
    [settings?.crm],
  );
}
